import { inject } from '@angular/core';
import type { CanActivateFn, UrlTree } from '@angular/router';
import { Router } from '@angular/router';

import { SessionService } from '@rpgc-ai/services/auth/session.service';
import { ModalService } from '@rpgc-ai/services/modal/modal.service';
import { EmailVerificationService } from '@rpgc-ai/services/profile/email-verification.service';
import { ProfileService } from '@rpgc-ai/services/profile/profile.service';
import { LanguageService } from '@rpgc-ai/services/translate/language.service';

import { AuthStore } from '@rpgc-ai/store/auth/auth.store';
import { ProfileStore } from '@rpgc-ai/store/profile/profile.store';

import { EModalName } from '@rpgc-ai/shared-interfaces/enums/modal-name.enum';
import { IS_SERVER } from '@rpgc-ai/shared-interfaces/tokens/is-server.token';

const EMAIL_VERIFICATION_REQUIRED_PATHS = new Set([
  '/blocked-creators',
  '/chat-history',
  '/create-chatbot',
  '/favorites',
  '/my-chatbots',
  '/my-personas',
]);

function isEmailVerificationRequiredUrl(url: string): boolean {
  const path = url.split(/[?#]/)[0].replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';

  return EMAIL_VERIFICATION_REQUIRED_PATHS.has(path) || path.startsWith('/chat/') || path.startsWith('/view-chatbot/');
}

/**
 * Пока профиль с сервера не известен, нельзя пускать на маршруты с tosGuard — иначе обход ToS/adult.
 */
async function waitForAuthenticatedUserProfile(
  profileStore: {
    profile: () => { id?: string | null };
    isLoading: () => boolean;
    error: () => string | null;
  },
  profileService: ProfileService,
): Promise<boolean> {
  if (profileStore.profile()?.id) {
    return true;
  }

  profileService.getProfile();

  const deadline = Date.now() + 15_000;
  let sawLoading = false;

  while (Date.now() < deadline) {
    const p = profileStore.profile();
    if (p?.id) {
      return true;
    }
    if (profileStore.error()) {
      return false;
    }
    if (profileStore.isLoading()) {
      sawLoading = true;
    } else if (sawLoading) {
      return false;
    }
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 25);
    });
  }

  return !!profileStore.profile()?.id;
}

/**
 * Guard для защиты маршрутов, требующих авторизации
 * Если пользователь не авторизован, перенаправляет на страницу входа
 */
export const authGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const authStore = inject(AuthStore);
  const sessionService = inject(SessionService);
  const router = inject(Router);
  const modalService = inject(ModalService);
  const languageService = inject(LanguageService);
  // 1) Уже авторизован в сторе
  if (authStore.isAuthenticated()) {
    return true;
  }

  // 2) Статус из SessionService уже известен (например, из TransferState)
  const ssrAuth = sessionService.isAuthenticated();
  if (ssrAuth === true) {
    return true;
  }

  // 3) Если статус ещё не определён, пробуем один раз загрузить сессию (SSR использует REQUEST headers)
  if (ssrAuth === undefined) {
    try {
      const data = await sessionService.getSession();
      if (data) {
        return true;
      }
    } catch {
      // ignore errors, fallback to redirect
    }
  }

  // 4) Не авторизован — открываем модалку входа и редиректим на корень языка
  const lang = languageService.getCurrentLanguage();
  void modalService.openModal('sign-in');
  return router.createUrlTree([lang]);
};

/**
 * Инвертированный guard для защиты маршрутов, требующих авторизации
 */
export const authGuardInverted: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const sessionService = inject(SessionService);
  const languageService = inject(LanguageService);

  // Статус из SessionService уже известен
  const ssrAuth = sessionService.isAuthenticated();

  if (ssrAuth === false) {
    return true; // Не авторизован - пропускаем
  }

  // Если статус ещё не определён, пробуем загрузить сессию
  if (ssrAuth === undefined) {
    try {
      const data = await sessionService.getSession();
      if (!data) {
        return true; // Сессия не найдена - пропускаем
      }
    } catch {
      return true; // При ошибке считаем неавторизованным
    }
  }

  // Пользователь авторизован - редиректим на главную языка
  const currentUrl = state.url;
  const langMatch = currentUrl.match(/^\/([a-z]{2})(\/|$)/);
  const lang = langMatch ? langMatch[1] : languageService.getCurrentLanguage() || 'en';

  return router.createUrlTree([lang]);
};

/**
 * Public routes still need resolved auth state before tosGuard checks mandatory user gates.
 * Confirmed anonymous users pass through.
 */
export const optionalSessionGuard: CanActivateFn = async (): Promise<boolean> => {
  const sessionService = inject(SessionService);
  const isServer = inject(IS_SERVER);

  if (isServer || sessionService.isAuthenticated() !== undefined) {
    return true;
  }

  await sessionService.getSession();
  return true;
};

export const tosGuard: CanActivateFn = async (_route, state): Promise<boolean | UrlTree> => {
  const profileStore = inject(ProfileStore);
  const authStore = inject(AuthStore);
  const sessionService = inject(SessionService);
  const profileService = inject(ProfileService);
  const modalService = inject(ModalService);
  const router = inject(Router);
  const emailVerificationService = inject(EmailVerificationService);
  const isServer = inject(IS_SERVER);

  if (isServer) {
    return true;
  }

  const sessionActive = authStore.isAuthenticated() || sessionService.isAuthenticated() === true;

  if (!sessionActive) {
    return true;
  }

  const profileOk = await waitForAuthenticatedUserProfile(profileStore, profileService);
  if (!profileOk) {
    return false;
  }

  const { isAdultConfirmed, isTermsAccepted, id, emailVerified } = profileStore.profile() ?? {};

  if (!!id && (!isAdultConfirmed || !isTermsAccepted)) {
    await modalService.openModal(EModalName.GUIDELINES);
    return false;
  }

  if (!!id && !emailVerified) {
    if (!isEmailVerificationRequiredUrl(state.url) || !emailVerificationService.shouldOpenMandatoryModal(state.url, router.url)) {
      return true;
    }
    emailVerificationService.markMandatoryModalOpened();
    await modalService.openModal(EModalName.EMAIL_VERIFICATION, ['confirm', 'mandatory'], { disableOverlayClose: true });
    return false;
  }

  return true;
};
